import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, UserPlus, Copy, Users, TrendingUp, Pencil, Trash2,
  Clock, Check, Link2, Gift, X
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const REWARD_TYPES = [
  { value: 'points', label: 'Points' },
  { value: 'discount', label: 'Discount' },
  { value: 'credit', label: 'Store Credit' },
  { value: 'gift', label: 'Free Gift' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function ReferralModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ code: '', referrer_name: '', referrer_email: '', reward_type: 'points', reward_value: 0, description: '', active: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ code: editing.code || '', referrer_name: editing.referrer_name || '', referrer_email: editing.referrer_email || '', reward_type: editing.reward_type || 'points', reward_value: editing.reward_value || 0, description: editing.description || '', active: editing.active !== false });
    else {
      const randomCode = `REF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      setForm({ code: randomCode, referrer_name: '', referrer_email: '', reward_type: 'points', reward_value: 100, description: '', active: true });
    }
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.referrer_name.trim()) { toast.error('Code and referrer name required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/retention/referrals/${editing.id}`, form)
        : await axios.post(`${API}/api/retention/referrals`, form);
      toast.success(editing ? 'Updated' : 'Referral created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'New'} Referral Code</DialogTitle><DialogDescription>Create a referral code for a customer</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Referral Code *</Label><Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value.toUpperCase()}))} placeholder="REF-ABC123" className="font-mono" data-testid="referral-code" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Referrer Name *</Label><Input value={form.referrer_name} onChange={e => setForm(p => ({...p, referrer_name: e.target.value}))} data-testid="referrer-name" /></div>
            <div><Label>Email</Label><Input value={form.referrer_email} onChange={e => setForm(p => ({...p, referrer_email: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reward Type</Label><Select value={form.reward_type} onValueChange={v => setForm(p => ({...p, reward_type: v}))}><SelectTrigger data-testid="reward-type"><SelectValue /></SelectTrigger><SelectContent>{REWARD_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Reward Value</Label><Input type="number" value={form.reward_value} onChange={e => setForm(p => ({...p, reward_value: parseFloat(e.target.value) || 0}))} data-testid="reward-value" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} /><Label className="text-sm">Active</Label></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-referral-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Referral'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

function ReferralDetail({ referral, onClose }) {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!referral) return;
    axios.get(`${API}/api/retention/referrals/${referral.id}/conversions`)
      .then(r => setConversions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [referral]);

  if (!referral) return null;

  const copyCode = () => {
    navigator.clipboard.writeText(referral.code).then(() => toast.success('Code copied!')).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="referral-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div><p className="text-xs text-gray-400">Referral Code</p><h2 className="text-lg font-bold font-mono text-gray-900">{referral.code}</h2></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyCode} className="gap-1" data-testid="copy-code-btn"><Copy className="h-3.5 w-3.5" /> Copy</Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="px-5 py-3 border-b">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={referral.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>{referral.active ? 'Active' : 'Inactive'}</Badge>
            <Badge variant="outline" className="text-xs">{referral.reward_type}: {referral.reward_value}</Badge>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span>By {referral.referrer_name}</span>
            {referral.referrer_email && <span>{referral.referrer_email}</span>}
          </div>
        </div>
        <div className="px-5 py-3 border-b grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{referral.conversions || 0}</p>
            <p className="text-[10px] text-blue-600">Conversions</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-green-700">{((referral.conversions || 0) * (referral.reward_value || 0)).toLocaleString()}</p>
            <p className="text-[10px] text-green-600">Total Rewards</p>
          </div>
        </div>
        <div className="px-5 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Conversions ({conversions.length})</p>
          {loading ? <div className="flex justify-center py-4"><div className="w-6 h-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div> : conversions.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No conversions yet</p>
          ) : (
            <div className="space-y-2">
              {conversions.map(c => (
                <div key={c.id} className="bg-gray-50 rounded-lg p-3" data-testid={`conversion-${c.id}`}>
                  <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-green-500" /><span className="text-sm font-semibold text-gray-700">{c.referred_name}</span><span className="text-[10px] text-gray-400">{timeAgo(c.created_at)}</span></div>
                  {c.referred_email && <p className="text-[10px] text-gray-400 ml-5">{c.referred_email}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReferralsPage() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedRef, setSelectedRef] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterActive !== 'all') params.active = filterActive === 'active';
      const { data } = await axios.get(`${API}/api/retention/referrals`, { params,  });
      setReferrals(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterActive]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaved = (r, isUpdate) => { if (isUpdate) setReferrals(p => p.map(x => x.id === r.id ? r : x)); else setReferrals(p => [r, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete referral?')) return; try { await axios.delete(`${API}/api/retention/referrals/${id}`); setReferrals(p => p.filter(r => r.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  const totalConversions = referrals.reduce((s, r) => s + (r.conversions || 0), 0);
  const activeCount = referrals.filter(r => r.active).length;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="referrals-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><UserPlus className="h-5 w-5 text-blue-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Referral Program<PageInfoTooltip pageKey="referrals" /></h1><p className="text-xs text-gray-400">{referrals.length} codes · {activeCount} active · {totalConversions} conversions</p></div></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-36 h-9 text-sm" data-testid="referral-search" /></div>
          <Select value={filterActive} onValueChange={setFilterActive}><SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid="new-referral-btn"><Plus className="h-4 w-4" /> New Referral</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div> : referrals.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No referral codes</h3><p className="text-sm text-gray-400 mb-4">Create referral codes for your customers</p><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> Create Referral</Button></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {referrals.map(r => (
            <Card key={r.id} className="border-gray-200 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setSelectedRef(r)} data-testid={`referral-row-${r.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${r.active ? 'bg-blue-50' : 'bg-gray-100'}`}>
                  <Link2 className={`h-5 w-5 ${r.active ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold font-mono text-gray-900">{r.code}</span>
                    <Badge className={r.active ? 'bg-green-100 text-green-700 text-[9px]' : 'bg-gray-100 text-gray-500 text-[9px]'}>{r.active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-400">
                    <span>{r.referrer_name}</span>
                    <span>{r.reward_type}: {r.reward_value}</span>
                    <span>{timeAgo(r.created_at)}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-gray-800">{r.conversions || 0}</p>
                  <p className="text-[10px] text-gray-400">conversions</p>
                </div>
                <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(r); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ReferralModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      {selectedRef && <ReferralDetail referral={selectedRef} onClose={() => setSelectedRef(null)} />}
    </div>
  );
}
